import { getPackageConfiguration } from 'frontend/api/services/packageSuppliersServices';
import Flex from 'frontend/commons/Flex';
import LoadSpinner from 'frontend/commons/spinner';
import IncludesRow from 'frontend/components/packages/includesRow';
import { DATE_KEYS, FALLBACK_IMAGE, QUERY_KEYS } from 'frontend/constants';
import { adultsQtyMapper } from 'frontend/helpers/julia-mappers';
import useInformation from 'frontend/hooks/useInformation';
import { getFormattedDate } from 'frontend/utils/dates';
import getFinalPrice from 'frontend/utils/get-final-price';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiShare } from 'react-icons/hi';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import { WhatsappIcon, WhatsappShareButton } from 'react-share';
import store from 'storeClient';
import ConsultaModal from '../paquetes/pages/components/ConsultaModal';
import ImageGallery from './components/ImageGallery';
import PackageHotelsInfo from './components/PackageHotelsInfo';
import PaymentSummaryRow from './components/PaymentSummaryRow';
import './styles.css';
import PackageDetailFlightsInfo from './components/FlightsInfo';
import moment from 'moment';

const getAdultsQtyText = (adultsQty) => {
  const mappedQty = adultsQtyMapper(adultsQty);
  if (mappedQty === 0) return '-';

  if (mappedQty === 1) {
    return '1 adulto';
  } else {
    return `${mappedQty} adultos`;
  }
};

const PackageDetail = (props) => {
  const { t } = useTranslation(['layout']);
  const id = props.match.params.id;
  const packageConfigurationId = id;

  const { web } = store.getState().applicationState;
  const url = web === 'localhost' ? 'http://localhost:3000' : `https://${web}`;
  const urlComplete = `${url}/packshare/${packageConfigurationId}`;
  const urlIntranet = `${url}/paquete/${packageConfigurationId}`;

  const { data: configuration, isLoading } = useQuery(
    [QUERY_KEYS.PACKAGE, id],
    () => getPackageConfiguration(packageConfigurationId),
    {
      enabled: !!packageConfigurationId,
    },
  );

  const images = useMemo(() => configuration?.package?.images, [configuration?.package]);

  const [consultaPackage, setConsultaPackage] = useState();

  const sharePackage = useCallback(async () => {
    navigator.clipboard.writeText(urlComplete);
    alert(t('layout:common.link_copied'));
  }, [urlComplete]);

  const information = useInformation(
    configuration?.package?.provider_id === 1
      ? configuration?.package?.packageDescriptions?.[0]?.description
      : undefined,
  );

  useEffect(() => {
    window.scrollTo({
      top: 0
    })
  }, [])

  return (
    <>
      {!!configuration?.package && !isLoading ? (
        <Flex vertical className="package-detail-container page-content-wrapper">
          <Flex className="page__header">
            <div className="banner__card__share">
              <span className="banner__card__share__text">Compartir</span>
              <Flex className="banner__card__share_section" onClick={sharePackage}>
                <HiShare className="banner__card__share__icon" />
              </Flex>
              <WhatsappShareButton url={urlComplete}>
                <Flex className="banner__card__share_section">
                  <WhatsappIcon size={32} round />
                </Flex>
              </WhatsappShareButton>
            </div>
          </Flex>
          <Flex className="boxes-wrapper">
            <Flex vertical className="boxes-wrapper package-information">
              <ImageGallery
                title={configuration.package.title}
                items={
                  configuration.package.provider_id === 1
                    ? [configuration?.package?.packageDestinations?.[0]?.destinationCity?.imageUrl]
                    : images ?? FALLBACK_IMAGE
                }
              />
              <Flex vertical className="card-box package-information__hotels-card">
                <h3 className="package-information__description-title">Hoteles</h3>
                <PackageHotelsInfo
                  {...configuration.package}
                  hotelInformation={configuration.hotelInformation}
                />
              </Flex>
              <Flex vertical className="card-box package-information__flights-card">
                <h3 className="package-information__description-title">Vuelos</h3>
                <PackageDetailFlightsInfo configuration={configuration} />
              </Flex>
              {!!information?.trim()?.length && (
                <Flex vertical className="card-box package-information__description-card">
                  {information}
                </Flex>
              )}
            </Flex>
            <Flex vertical className="boxes-wrapper package-details">
              <Flex vertical className="package-details-cards">
                <Flex vertical className="card-box package-details__summary-card">
                  <Flex className="package-details__summary-card__header with-bottom-divider">
                    <h4 className="package-information__summary-title">
                      {t('packages.details.package_detail')}
                    </h4>
                  </Flex>
                  <Flex
                    vertical
                    className="package-details__summary-card__content with-bottom-divider"
                  >
                    <p style={{ fontWeight: 500 }}>
                      {configuration.package.packageDestinations?.[0]?.destinationCity.nombreCiudad}
                    </p>
                    <Flex vertical>
                      <p>
                        Desde{' '}
                        {configuration.package.packageOrigins[0].originExporta.nombreCiudad.trim()}
                      </p>
                      <p>
                        Salida{' '}
                        {moment(
                          configuration.flight.tripInformation.find(({ order }) => order === 1)
                            .departureDate,
                        )
                          .utc()
                          .format(DATE_KEYS.FORMAT.DAY_SHORT)}
                      </p>
                      <p style={{ fontWeight: 500 }}>
                        {getAdultsQtyText(configuration.hotelInformation[0].occupation)}
                      </p>
                    </Flex>
                  </Flex>
                  <Flex vertical className="package-details__summary-card__footer">
                    <IncludesRow />
                  </Flex>
                </Flex>
                <Flex vertical className="card-box package-details__payment-card">
                  <Flex className="package-details__payment-card__header with-bottom-divider">
                    <p>Precio total</p>
                    <p
                      style={{
                        fontWeight: 600,
                        fontSize: '1.125rem',
                      }}
                    >
                      {getFinalPrice({
                        currencyCode: configuration.package.currencyCode,
                        total: configuration.totalManager.total,
                      })}
                    </p>
                  </Flex>
                  <Flex
                    vertical
                    className="package-details__payment-card__content with-bottom-divider"
                  >
                    <PaymentSummaryRow
                      label="Precio del producto"
                      value={getFinalPrice({
                        currencyCode: configuration.package.currencyCode,
                        total: configuration.totalManager.subTotal,
                      })}
                    />
                    <PaymentSummaryRow
                      label="Impuestos y tasas"
                      value={getFinalPrice({
                        currencyCode: configuration.package.currencyCode,
                        total: configuration.totalManager.taxTotal,
                      })}
                    />
                  </Flex>
                  <div className="package-details__payment-card__notice">
                    <div className="title">Nota</div>
                    <div className="content">
                      PARA TARIFAS EXPRESADAS EN DÓLAR ESTADOUNIDENSE (USD). EN CASO DE ABONAR EN PESOS (ARS) SE APLICARÁ EL TIPO DE CAMBIO CORRESPONDIENTE AL DÍA DE PAGO, Y SE ADICIONARÁ LA PERCEPCIÓN CORRESPONDIENTE (RG 4815).
                    </div>
                  </div>
                </Flex>
              </Flex>
              <Flex className="package-details-buttons" style={{ gap: '1rem' }}>
                <button
                  className="package-details-button package-details__ask-button"
                  onClick={() => {
                    setConsultaPackage({
                      ...configuration.package,
                      hotelInformation: configuration.hotelInformation,
                      total: getFinalPrice({
                        currencyCode: configuration.package.currencyCode,
                        total: configuration.totalManager.total,
                      }),
                      providerName: configuration.package.provider.name,
                      link: urlIntranet,
                    });
                  }}
                >
                  Consultar
                </button>
              </Flex>
            </Flex>
          </Flex>
          <ConsultaModal
            consultaPackage={consultaPackage}
            onClose={() => {
              setConsultaPackage(undefined);
            }}
          />
        </Flex>
      ) : (
        <Flex
          style={{
            flex: 1,
            height: '30rem',
            justifyContent: 'center',
            padding: '8rem 0',
          }}
        >
          <LoadSpinner />
        </Flex>
      )}
    </>
  );
};

export default PackageDetail;
